import { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import QRCode from 'qrcode.react';
import { Trans, useTranslation } from 'react-i18next';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { FiChevronLeft, FiMonitor } from 'react-icons/fi';

import { runtimeConfig } from '@kiper/fns';
import { useSwal } from '@kiper/hooks';
import { Spinner } from '@kiper/ui';
import { Col, Title, Subtitle, ButtonSubmit } from '../styles';
import { isMfaEnrollmentSessionExpired } from '../mfaEnrollmentErrors';
import { clearMfaFlow } from '../mfaFlowStorage';
import {
  StepHeader,
  BackButton,
  ProgressGroup,
  ProgressTrack,
  ProgressSegment,
  StepLabel,
  ChipRow,
  AppChip,
  QrPanel,
  QrFrame,
  QrCaption,
  StoreButtons,
  StoreButton,
} from './styles';

// Base do link inteligente: o `account/server` expõe /r/auth-app, irmão do
// /graphql, que redireciona para a loja certa conforme o sistema do celular.
const REDIRECT_BASE = (runtimeConfig.RAZZLE_GRAPHQL_URL || '').replace(
  /\/graphql\/?$/,
  '',
);

// Apps autenticadores suportados (URLs de loja para os botões diretos).
const APPS = {
  google: {
    label: 'Google Authenticator',
    appStoreUrl: 'https://apps.apple.com/app/google-authenticator/id388497605',
    playStoreUrl:
      'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
  },
  microsoft: {
    label: 'Microsoft',
    appStoreUrl:
      'https://apps.apple.com/app/microsoft-authenticator/id983156458',
    playStoreUrl:
      'https://play.google.com/store/apps/details?id=com.azure.authenticator',
  },
  // Ente: o link desktop resolve o instalador do SO via redirect do server.
  ente: {
    label: 'Ente Auth',
    appStoreUrl: 'https://apps.apple.com/app/ente-auth/id6444121398',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=io.ente.auth',
    desktopUrl: `${REDIRECT_BASE}/r/auth-app-desktop`,
  },
};
const APP_ORDER = ['google', 'microsoft', 'ente'];

// SO só para rotular o botão ("Baixar para {os}"); o download real é resolvido
// pelo server. Guard de SSR: no server (Razzle) não existe `navigator`.
const detectDesktopOs = () => {
  if (typeof navigator === 'undefined') return null;
  const { userAgent } = navigator;
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Mac OS X|Macintosh/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent) && !/Android/i.test(userAgent)) return 'Linux';
  return null;
};

const LoginMfaInstallApp = ({
  values,
  auth,
  setFieldValue,
  setCurrentStep,
  steps,
}) => {
  const [t] = useTranslation('login');
  const { toast } = useSwal();
  const [selectedApp, setSelectedApp] = useState('google');
  const [loading, setLoading] = useState(false);
  const [desktopOs, setDesktopOs] = useState(null);

  useEffect(() => {
    setDesktopOs(detectDesktopOs());
  }, []);

  const app = APPS[selectedApp];
  const qrValue = `${REDIRECT_BASE}/r/auth-app?app=${selectedApp}`;
  const desktopLabel = t('two-step.install.download-for', {
    os: desktopOs || t('two-step.install.desktop-generic'),
  });

  const handleContinue = async () => {
    setLoading(true);
    try {
      const association = values.mfaEnrollmentSession
        ? await auth.associateMfaEnrollment(values.mfaEnrollmentSession)
        : await auth.associateToken();
      setFieldValue('mfaSecret', association?.secret);
      setFieldValue('mfaOtpUri', association?.otpAuthUri);
      setFieldValue('mfaApp', selectedApp);
      setCurrentStep(steps.mfaEnroll);
    } catch (error) {
      // Sessão de enrollment expirada → volta para a senha com aviso.
      if (values.mfaEnrollmentSession && isMfaEnrollmentSessionExpired(error)) {
        setFieldValue('mfaEnrollmentSession', null);
        clearMfaFlow();
        toast.fire({ title: t('two-step.session-expired'), icon: 'error' });
        setCurrentStep(steps.password);
        return;
      }
      // No modo obrigatório não há saída alternativa, então qualquer outro
      // erro ganha um toast genérico e a pessoa fica na tela para tentar de
      // novo. No modo opcional o erro segue silencioso, como sempre foi.
      if (values.mfaEnrollmentSession) {
        toast.fire({ title: t('two-step.generic-error'), icon: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Col>
      <StepHeader>
        <BackButton
          type="button"
          onClick={() => setCurrentStep(steps.mfaEnrollInvite)}
        >
          <FiChevronLeft size={16} />
          {t('two-step.back')}
        </BackButton>
        <ProgressGroup>
          <ProgressTrack>
            <ProgressSegment $done />
            <ProgressSegment />
            <ProgressSegment />
          </ProgressTrack>
          <StepLabel>
            {t('two-step.step-progress', { current: 1, total: 3 })}
          </StepLabel>
        </ProgressGroup>
      </StepHeader>

      <Title>{t('two-step.install.title')}</Title>
      <Subtitle>{t('two-step.install.subtitle')}</Subtitle>

      <ChipRow>
        {APP_ORDER.map(id => (
          <AppChip
            key={id}
            type="button"
            $active={selectedApp === id}
            onClick={() => setSelectedApp(id)}
          >
            {APPS[id].label}
          </AppChip>
        ))}
      </ChipRow>

      <QrPanel>
        <QrCaption>
          <Trans
            t={t}
            i18nKey="two-step.install.qr-caption"
            values={{ app: app.label }}
          />
        </QrCaption>
        <QrFrame>
          <QRCode value={qrValue} size={132} />
        </QrFrame>
      </QrPanel>

      <StoreButtons>
        <StoreButton
          href={app.appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaApple size={15} />
          App Store
        </StoreButton>
        <StoreButton
          href={app.playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaGooglePlay size={15} />
          Google Play
        </StoreButton>
        {app.desktopUrl && (
          <StoreButton
            href={app.desktopUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiMonitor size={15} />
            {desktopLabel}
          </StoreButton>
        )}
      </StoreButtons>

      <ButtonSubmit
        block
        color="primary"
        onClick={handleContinue}
        loading={loading ? 1 : 0}
      >
        {t('two-step.install.continue')}
        {loading && <Spinner size={16} />}
      </ButtonSubmit>
    </Col>
  );
};

export default LoginMfaInstallApp;

LoginMfaInstallApp.propTypes = {
  values: propTypes.object.isRequired,
  auth: propTypes.object.isRequired,
  setFieldValue: propTypes.func.isRequired,
  setCurrentStep: propTypes.func.isRequired,
  steps: propTypes.object.isRequired,
};
